// location-preview-dialog.component.ts
import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { LocationWorkService, BulkImportRequest, ImportLocationData, BulkImportResponse } from '../../services/location-work.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ProcessingDialogComponent } from '../processing-dialog/processing-dialog.component';
import { HttpErrorResponse } from '@angular/common/http';
import { take } from 'rxjs/operators';

export interface LocationRow {
  lineNumber: number;
  isValid: boolean;
  [key: string]: any;
}

export interface PreviewDialogData {
  locations: LocationRow[];
  fileHeaders: string[];
  requiredColumns: string[];
  organizationId: number;
  organizationName: string;
  timeZoneId: number;
  userId: number;
  userName: string;
  fileName: string;
  fileDate: Date;
  billableOrgId: number;
  validationError?: string | null;
}

@Component({
  selector: 'app-location-preview-dialog',
  templateUrl: './location-preview-dialog.component.html',
  styleUrls: ['./location-preview-dialog.component.css']
})
export class LocationPreviewDialogComponent {
  dataSource: MatTableDataSource<LocationRow>;
  displayedColumns: string[] = [];
  fileHeaders: string[] = [];
  requiredColumns: string[] = [];
  isImporting = false;

  importDone = false;
  importResponse: BulkImportResponse | null = null;
  validationError: string | null = null;

  constructor(
    public dialogRef: MatDialogRef<LocationPreviewDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: PreviewDialogData,
    private locationWorkService: LocationWorkService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) {
    this.fileHeaders = data.fileHeaders || [];
    this.requiredColumns = data.requiredColumns || [];
    this.displayedColumns = ['lineNumber', ...this.fileHeaders];
    this.dataSource = new MatTableDataSource(data.locations || []);
    this.validationError = data.validationError || null;

    if (this.validationError) {
      this.importDone = true;
      this.importResponse = this.buildValidationErrorResponse(this.validationError);
    }
  }

  get totalRows(): number { return this.dataSource.data.length; }
  get validRows(): number { return this.dataSource.data.filter(row => row.isValid).length; }
  get invalidRows(): number { return this.dataSource.data.filter(row => !row.isValid).length; }
  get hasErrors(): boolean {
    const results = this.importResponse?.results ?? [];
    return results.some(r => (r.status || '').toLowerCase() === 'error' || !!(r as any).errorMessage);
  }

  onConfirm() {
    this.dialogRef.updateSize('50vw', '50vh');
    this.dialogRef.updatePosition();
    this.performBulkImport();
  }

  private performBulkImport(): void {
    this.isImporting = true;

    const procRef = this.dialog.open(ProcessingDialogComponent, {
      width: '400px',
      disableClose: true,
      data: { title: 'Processing', message: 'Importing file to database', total: this.totalRows }
    });

    const allLocations = this.dataSource.data;
    const importLocations: ImportLocationData[] = allLocations.map(row => ({
      lineNumber: row.lineNumber,
      locationName: row['Location Name'] || '',
      address1: row['Address1'] || '',
      address2: row['Address2'] || '',
      city: row['City'] || '',
      state: row['State'] || '',
      postalCode: row['PostalCode'] || row['Postal Code'] || '',
      country: row['Country'] || '',
      userLocationNumber: row['User Location Number'] || ''
    }));

    const bulkImportRequest: BulkImportRequest = {
      locations: importLocations,
      organizationId: this.data.organizationId,
      organizationName: this.data.organizationName,
      timeZoneId: this.data.timeZoneId,
      userId: this.data.userId,
      userName: this.data.userName,
      fileName: this.data.fileName,
      fileDate: this.data.fileDate,
      billableOrganizationID: this.data.billableOrgId
    };

    this.locationWorkService.bulkImport(bulkImportRequest)
      .pipe(take(1))
      .subscribe({
        next: (resp: BulkImportResponse) => {
          this.isImporting = false;
          procRef.close();

          const hasErrors = this.responseHasErrors(resp);
          if (!hasErrors) {
            this.dialogRef.close(true);
            return;
          }

          this.importResponse = resp;
          this.importDone = true;
          this.snackBar.open(resp.message || 'Import finished', 'Close', { duration: 4000 });
        },
        error: (error) => {
          this.isImporting = false;
          procRef.close();
          const friendly = this.getFriendlyErrorMessage(error);
          this.snackBar.open(friendly, 'Close', { duration: 6000 });
        }
      });
  }

  private responseHasErrors(resp: BulkImportResponse | null | undefined): boolean {
    if (!resp) return true;
    if (typeof (resp as any).errorCount === 'number') return (resp as any).errorCount > 0;
    const results = resp.results || [];
    return results.some(r => (r.status || '').toLowerCase() === 'error' || !!(r as any).errorMessage);
  }

  private buildValidationErrorResponse(message: string): BulkImportResponse {
    return {
      message,
      totalProcessed: 0,
      successCount: 0,
      errorCount: 1,
      results: [
        {
          lineNumber: 0,
          locationName: '',
          locationId: '',
          address1: '',
          address2: '',
          city: '',
          state: '',
          postalCode: '',
          country: '',
          organizationName: this.data.organizationName || '',
          destinationOrganizationName: '',
          timeStamp: new Date(),
          userName: this.data.userName || '',
          status: 'Error',
          errorMessage: message,
          userLocationNumber: ''
        }
      ]
    };
  }

  onCancel() { this.dialogRef.close(this.importDone ? true : false); }

  async onDownloadResults(): Promise<void> {
    if (!this.importResponse?.results?.length) return;

    const headers = [
      'Location Name','Billable Org','Destination Organization','Time Stamp','User Name',
      'LocationName','Address1','Address2','City','State','PostalCode','Country','Status','Result','Error Message'
    ];

    const rows = (this.importResponse.results as any[]).map(r => {
      const text = (r.errorMessage || '').toString();
      const isDup = /duplicate/i.test(text);
      const fromBatch = /duplicate in import file/i.test(text);
      const fromDb = /duplicate in system/i.test(text);
      const resultText = isDup ? `Duplicate${fromBatch ? ' (Batch)' : ''}${fromDb ? ' (Database)' : ''}` : (text ? 'Error' : '');

      return [
        r.locationName || '',
        r.organizationName || '',
        r.destinationOrganizationName || '',
        r.timeStamp ? new Date(r.timeStamp).toLocaleString() : '',
        r.userName || '',
        r.locationName || '',
        r.address1 || '',
        r.address2 || '',
        r.city || '',
        r.state || '',
        r.postalCode || '',
        r.country || '',
        (r.status || '').toString().toUpperCase(),
        resultText,
        r.errorMessage || ''
      ];
    });

    const XLSX = await import('xlsx');
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Import Results');
    const fileBase = (this.data.fileName || 'import-results').replace(/\.[^/.]+$/, '');
    XLSX.writeFile(wb, `${fileBase}-results.xlsx`);
  }

  private getFriendlyErrorMessage(err: unknown): string {
    const httpErr = err as any;
    const status = httpErr?.status ?? 0;
    if (status === 0) return 'We could not reach the server. Please check your connection and try again.';
    if (status === 400) return 'The file or data is invalid. Please review and try again.';
    if (status === 401) return 'Your session has expired. Please sign in and try again.';
    if (status === 403) return 'You do not have permission to perform this action.';
    if (status === 413) return 'The file is too large. Please upload a smaller file.';
    if (status === 415) return 'Unsupported file format.';
    if (status === 429) return 'Too many requests. Please wait a moment and try again.';
    if (status >= 500) return 'A server error occurred. Please try again later.';
    return 'Import failed. Please try again.';
  }

  isRequiredColumn(columnName: string): boolean { return this.requiredColumns.includes(columnName); }
  getCellValue(row: LocationRow, columnName: string): any { return row[columnName] || ''; }
  hasRequiredFieldError(row: LocationRow, columnName: string): boolean {
    if (!this.isRequiredColumn(columnName)) return false;
    const value = this.getCellValue(row, columnName);
    return !value || value.toString().trim() === '';
  }
}
